export type Locale = "en" | "ms";

export const SUPPORTED_LOCALES: { value: Locale; label: string }[] = [
  { value: "en", label: "English" },
  { value: "ms", label: "Bahasa Malaysia" },
];

type Dict = Record<string, string>;

const en: Dict = {
  // Common
  "common.save": "Save changes",
  "common.cancel": "Cancel",
  "common.reset": "Reset",
  "common.saving": "Saving…",
  "common.loading": "Loading…",
  "common.enabled": "Enabled",
  "common.disabled": "Disabled",

  // Sidebar / nav
  "nav.dashboard": "Dashboard",
  "nav.attendance": "Attendance Monitor",
  "nav.requests": "Requests & Approvals",
  "nav.employees": "Employees",
  "nav.departments": "Org Structure",
  "nav.sites": "Sites & Geofences",
  "nav.shifts": "Shifts",
  "nav.roster": "Roster",
  "nav.holidays": "Public Holidays",
  "nav.reports": "Reports",
  "nav.users": "Users & Roles",
  "nav.settings": "Settings",
  "nav.notifications": "Notifications",
  "nav.profile": "Profile",
  "nav.aiAssistant": "AI Assistant",

  // Settings page
  "settings.title": "Settings",
  "settings.tabs.general": "General",
  "settings.tabs.rules": "Attendance Rules",
  "settings.tabs.hours": "Working Hours",
  "settings.tabs.geo": "Geofence",
  "settings.tabs.notif": "Notifications",
  "settings.tabs.roles": "Roles",
  "settings.tabs.data": "Data",
  "settings.tabs.ai": "AI Assistant",

  "settings.general.title": "Organization",
  "settings.general.desc": "Branding and locale defaults for the admin portal.",
  "settings.general.orgName": "Organization name",
  "settings.general.language": "Default language",
  "settings.general.timezone": "Timezone",
  "settings.general.dateFormat": "Date format",

  "settings.rules.title": "Attendance rules",
  "settings.rules.desc": "Thresholds for late, partial and missed attendance.",
  "settings.rules.grace": "Grace period (minutes)",
  "settings.rules.late": "Late threshold (minutes)",
  "settings.rules.minHours": "Min work hours/day",
  "settings.rules.requireApproval": "Require approval for corrections",
  "settings.rules.requireApprovalDesc": "Manager sign-off required for any retroactive change.",
  "settings.rules.autoFlag": "Auto-flag exceptions",
  "settings.rules.autoFlagDesc": "System highlights anomalies for the operations queue.",

  "settings.hours.title": "Default working hours",
  "settings.hours.desc": "Fallback hours if no shift schedule is assigned.",
  "settings.hours.start": "Start time",
  "settings.hours.end": "End time",

  "settings.geo.title": "Geofencing policy",
  "settings.geo.desc": "Global enforcement defaults for site-based attendance.",
  "settings.geo.enforce": "Geofence enforcement enabled",
  "settings.geo.enforceDesc": "Block check-ins outside defined site perimeter.",
  "settings.geo.gpsFallback": "Allow GPS-only fallback",
  "settings.geo.gpsFallbackDesc": "When geofence data is unavailable, accept GPS coordinates.",
  "settings.geo.defaultRadius": "Default geofence radius (m)",
  "settings.geo.wfh": "Allow work from home (WFH)",
  "settings.geo.wfhDesc": "Employees can check in from their registered home location. Sites can override this per-site.",
  "settings.geo.homeRadius": "Default home radius (m)",
  "settings.hours.workingDays": "Working days",
  "settings.hours.workingDaysDesc": "Days the organisation operates. Missed-attendance notifications are not sent on other days \u2014 without this, employees are told they missed attendance every weekend.",
  "settings.geo.wfa": "Allow Work From Anywhere applications",
  "settings.geo.wfaDesc": "Employees can apply for Work From Anywhere over a date range. Once an application is approved, that employee may check in from any location on those dates and the attendance is recorded as WFA, with the approval linked to it. Leave this off and an approved application is only a record \u2014 it does not change check-in.",
  "settings.geo.webCheckIn": "Allow check-in from a web browser",
  "settings.geo.webCheckInDesc": "Employees can clock in and out from the GeoKerja website as well as the mobile app. Browser location is usually less precise than a phone's GPS, so web punches record the accuracy they were taken with.",
  "settings.geo.maxAccuracy": "Worst accepted GPS accuracy (m)",
  "settings.geo.maxAccuracyDesc": "A position reported as less accurate than this cannot place someone inside a geofence. Such punches are marked low-accuracy, and are rejected outright at sites that enforce a geofence with no GPS-only fallback.",

  "settings.notif.title": "Notification channels",
  "settings.notif.desc": "Where alerts are routed for each event type.",
  "settings.notif.email": "Email digest",
  "settings.notif.emailDesc": "Daily 8am summary to administrators.",
  "settings.notif.inApp": "In-app notifications",
  "settings.notif.inAppDesc": "Real-time alerts in the portal.",
  "settings.notif.push": "Mobile push",
  "settings.notif.pushDesc": "Send to admin mobile app.",

  "settings.ai.title": "AI Assistant",
  "settings.ai.desc": "Manage the embedded GeoKerja Assistant.",
  "settings.ai.enableHR": "Enable AI Assistant for HR",
  "settings.ai.enableHRDesc": "HR personas can query the assistant.",
  "settings.ai.enableSup": "Enable AI Assistant for Supervisors",
  "settings.ai.enableSupDesc": "Site supervisors can query the assistant.",
  "settings.ai.enableMobile": "Enable AI Assistant for Mobile users",
  "settings.ai.enableMobileDesc": "Workforce app embedded assistant.",
  "settings.ai.responseStyle": "Default response style",
  "settings.ai.concise": "Concise",
  "settings.ai.detailed": "Detailed",

  "settings.roles.title": "Role permissions",
  "settings.roles.desc": "Map admin personas to capabilities.",

  "settings.data.title": "Data export",
  "settings.data.desc": "Export records for compliance or archival.",
  "settings.data.exportAttendance": "Export attendance (CSV)",
  "settings.data.exportEmployees": "Export employees (CSV)",

  "settings.saved": "Settings saved",
  "settings.tabs.requests": "Requests",
  "settings.requests.approvalTitle": "Who decides requests",
  "settings.requests.approvalDesc": "When on, each staff member's reporting manager approves their requests in My team on the web portal. HR can still change any decision.",
  "settings.requests.approvalToggle": "Line managers approve their direct reports' requests",
  "settings.requests.attachmentsTitle": "Required attachments",
  "settings.requests.attachmentsDesc": "Choose which request types need a file, and tell staff what to attach. Off for every type by default.",
  "settings.requests.type": "Request type",
  "settings.requests.require": "Require attachment",
  "settings.requests.instruction": "Instruction shown to staff (optional)",
  "settings.requests.instructionPlaceholder": "e.g. what staff should attach",
  "settings.requests.typesTitle": "Request types",
  "settings.requests.typesDesc": "Choose which types staff can raise, what each is called, and whether it needs an attachment. Names and descriptions are shown to staff exactly as you write them.",
  "settings.requests.offered": "Offered to staff",
  "settings.requests.name": "Name",
  "settings.requests.description": "Description",
  "settings.requests.descriptionPlaceholder": "Shown under the name (optional)",
  "settings.requests.dates": "Dates",
  "settings.requests.single": "Single day",
  "settings.requests.range": "Date range",
  "settings.requests.lastOffered": "At least one type must stay on",
  "settings.requests.wfaNote": "Staff see it only while Work from anywhere is on in Attendance settings.",
  "settings.saveFailed": "Could not save settings",
};

const ms: Dict = {
  "common.save": "Simpan perubahan",
  "common.cancel": "Batal",
  "common.reset": "Set semula",
  "common.saving": "Menyimpan…",
  "common.loading": "Memuatkan…",
  "common.enabled": "Dihidupkan",
  "common.disabled": "Dimatikan",

  "nav.dashboard": "Papan Pemuka",
  "nav.attendance": "Pemantau Kehadiran",
  "nav.requests": "Permohonan & Kelulusan",
  "nav.employees": "Pekerja",
  "nav.departments": "Struktur Organisasi",
  "nav.sites": "Lokasi & Geofence",
  "nav.shifts": "Syif",
  "nav.roster": "Jadual Syif",
  "nav.holidays": "Cuti Umum",
  "nav.reports": "Laporan",
  "nav.users": "Pengguna & Peranan",
  "nav.settings": "Tetapan",
  "nav.notifications": "Pemberitahuan",
  "nav.profile": "Profil",
  "nav.aiAssistant": "Pembantu AI",

  "settings.title": "Tetapan",
  "settings.tabs.general": "Umum",
  "settings.tabs.rules": "Peraturan Kehadiran",
  "settings.tabs.hours": "Waktu Kerja",
  "settings.tabs.geo": "Geofence",
  "settings.tabs.notif": "Pemberitahuan",
  "settings.tabs.roles": "Peranan",
  "settings.tabs.data": "Data",
  "settings.tabs.ai": "Pembantu AI",

  "settings.general.title": "Organisasi",
  "settings.general.desc": "Penjenamaan dan tetapan lokaliti portal pentadbir.",
  "settings.general.orgName": "Nama organisasi",
  "settings.general.language": "Bahasa lalai",
  "settings.general.timezone": "Zon masa",
  "settings.general.dateFormat": "Format tarikh",

  "settings.rules.title": "Peraturan kehadiran",
  "settings.rules.desc": "Ambang untuk lewat, separa dan kehadiran terlepas.",
  "settings.rules.grace": "Tempoh tenggang (minit)",
  "settings.rules.late": "Ambang lewat (minit)",
  "settings.rules.minHours": "Jam kerja minimum/hari",
  "settings.rules.requireApproval": "Perlukan kelulusan untuk pembetulan",
  "settings.rules.requireApprovalDesc": "Pengesahan pengurus diperlukan untuk sebarang pindaan retroaktif.",
  "settings.rules.autoFlag": "Auto-tanda pengecualian",
  "settings.rules.autoFlagDesc": "Sistem menyerlahkan anomali untuk baris gilir operasi.",

  "settings.hours.title": "Waktu kerja lalai",
  "settings.hours.desc": "Waktu sandaran jika tiada jadual syif diberikan.",
  "settings.hours.start": "Masa mula",
  "settings.hours.end": "Masa tamat",

  "settings.geo.title": "Polisi geofencing",
  "settings.geo.desc": "Penguatkuasaan global lalai untuk kehadiran berasaskan lokasi.",
  "settings.geo.enforce": "Penguatkuasaan geofence dihidupkan",
  "settings.geo.enforceDesc": "Sekat daftar masuk di luar perimeter lokasi yang ditetapkan.",
  "settings.geo.gpsFallback": "Benarkan sandaran GPS sahaja",
  "settings.geo.gpsFallbackDesc": "Apabila data geofence tiada, terima koordinat GPS.",
  "settings.geo.defaultRadius": "Radius geofence lalai (m)",
  "settings.geo.wfh": "Benarkan bekerja dari rumah (WFH)",
  "settings.geo.wfhDesc": "Pekerja boleh daftar masuk dari lokasi rumah berdaftar. Setiap lokasi boleh menolak tetapan ini.",
  "settings.geo.homeRadius": "Radius rumah lalai (m)",
  "settings.hours.workingDays": "Hari bekerja",
  "settings.hours.workingDaysDesc": "Hari organisasi beroperasi. Pemberitahuan ketidakhadiran tidak dihantar pada hari lain \u2014 tanpa tetapan ini, pekerja diberitahu mereka tidak hadir setiap hujung minggu.",
  "settings.geo.wfa": "Benarkan permohonan Bekerja Dari Mana-Mana (WFA)",
  "settings.geo.wfaDesc": "Pekerja boleh memohon Bekerja Dari Mana-Mana bagi satu julat tarikh. Setelah permohonan diluluskan, pekerja itu boleh daftar masuk dari mana-mana lokasi pada tarikh tersebut dan kehadiran direkodkan sebagai WFA, dengan kelulusan berkenaan dipautkan kepadanya. Jika tetapan ini dimatikan, permohonan yang diluluskan hanyalah rekod \u2014 ia tidak mengubah daftar masuk.",
  "settings.geo.webCheckIn": "Benarkan daftar masuk melalui pelayar web",
  "settings.geo.webCheckInDesc": "Pekerja boleh daftar masuk dan keluar dari laman web GeoKerja selain aplikasi mudah alih. Lokasi pelayar biasanya kurang tepat berbanding GPS telefon, jadi setiap daftar masuk web merekodkan ketepatan yang diperoleh.",
  "settings.geo.maxAccuracy": "Ketepatan GPS terburuk yang diterima (m)",
  "settings.geo.maxAccuracyDesc": "Kedudukan yang dilaporkan kurang tepat daripada nilai ini tidak boleh mengesahkan seseorang berada dalam pagar geo. Daftar masuk sebegini ditanda ketepatan rendah, dan ditolak terus di lokasi yang menguatkuasakan pagar geo tanpa sandaran GPS sahaja.",

  "settings.notif.title": "Saluran pemberitahuan",
  "settings.notif.desc": "Ke mana makluman dihalakan bagi setiap jenis peristiwa.",
  "settings.notif.email": "Ringkasan e-mel",
  "settings.notif.emailDesc": "Ringkasan harian 8 pagi kepada pentadbir.",
  "settings.notif.inApp": "Pemberitahuan dalam aplikasi",
  "settings.notif.inAppDesc": "Amaran masa nyata dalam portal.",
  "settings.notif.push": "Push mudah alih",
  "settings.notif.pushDesc": "Hantar ke aplikasi mudah alih pentadbir.",

  "settings.ai.title": "Pembantu AI",
  "settings.ai.desc": "Urus Pembantu GeoKerja terbenam.",
  "settings.ai.enableHR": "Hidupkan Pembantu AI untuk HR",
  "settings.ai.enableHRDesc": "Persona HR boleh menanya pembantu.",
  "settings.ai.enableSup": "Hidupkan Pembantu AI untuk Penyelia",
  "settings.ai.enableSupDesc": "Penyelia lokasi boleh menanya pembantu.",
  "settings.ai.enableMobile": "Hidupkan Pembantu AI untuk pengguna Mudah Alih",
  "settings.ai.enableMobileDesc": "Pembantu terbenam aplikasi tenaga kerja.",
  "settings.ai.responseStyle": "Gaya respons lalai",
  "settings.ai.concise": "Ringkas",
  "settings.ai.detailed": "Terperinci",

  "settings.roles.title": "Kebenaran peranan",
  "settings.roles.desc": "Petakan persona pentadbir kepada keupayaan.",

  "settings.data.title": "Eksport data",
  "settings.data.desc": "Eksport rekod untuk pematuhan atau arkib.",
  "settings.data.exportAttendance": "Eksport kehadiran (CSV)",
  "settings.data.exportEmployees": "Eksport pekerja (CSV)",

  "settings.saved": "Tetapan disimpan",
  "settings.tabs.requests": "Permohonan",
  "settings.requests.approvalTitle": "Siapa yang memutuskan permohonan",
  "settings.requests.approvalDesc": "Apabila dihidupkan, pengurus kepada setiap kakitangan meluluskan permohonan mereka dalam Pasukan Saya di portal web. HR masih boleh mengubah sebarang keputusan.",
  "settings.requests.approvalToggle": "Pengurus meluluskan permohonan kakitangan di bawah seliaan langsung mereka",
  "settings.requests.attachmentsTitle": "Lampiran wajib",
  "settings.requests.attachmentsDesc": "Pilih jenis permohonan yang memerlukan fail, dan beritahu kakitangan apa yang perlu dilampirkan. Dimatikan untuk semua jenis secara lalai.",
  "settings.requests.type": "Jenis permohonan",
  "settings.requests.require": "Wajibkan lampiran",
  "settings.requests.instruction": "Arahan kepada kakitangan (pilihan)",
  "settings.requests.instructionPlaceholder": "cth. apa yang perlu dilampirkan oleh kakitangan",
  "settings.requests.typesTitle": "Jenis permohonan",
  "settings.requests.typesDesc": "Pilih jenis yang boleh dimohon oleh kakitangan, nama setiap jenis, dan sama ada ia memerlukan lampiran. Nama dan keterangan dipaparkan kepada kakitangan sama seperti yang anda tulis.",
  "settings.requests.offered": "Ditawarkan kepada kakitangan",
  "settings.requests.name": "Nama",
  "settings.requests.description": "Keterangan",
  "settings.requests.descriptionPlaceholder": "Dipaparkan di bawah nama (pilihan)",
  "settings.requests.dates": "Tarikh",
  "settings.requests.single": "Satu hari",
  "settings.requests.range": "Julat tarikh",
  "settings.requests.lastOffered": "Sekurang-kurangnya satu jenis mesti kekal dihidupkan",
  "settings.requests.wfaNote": "Kakitangan hanya melihatnya apabila Kerja dari mana-mana dihidupkan dalam tetapan Kehadiran.",
  "settings.saveFailed": "Tidak dapat menyimpan tetapan",
};

export const DICTIONARIES: Record<Locale, Dict> = { en, ms };

export function translate(locale: Locale, key: string): string {
  return DICTIONARIES[locale]?.[key] ?? DICTIONARIES.en[key] ?? key;
}
